import React, { FC, useContext, useState } from "react";

// interfaces, helpers, & children components
import { Alert } from "components/common/Alert/Alert";
import { Button } from "components/Button";
import { CartRemoveItemSvg } from "components/icons/CartRemoveItemSvg";
import { ITEM_BRAND, endpoints } from "config";
import { CartContext } from "context/cart";
import { LocalizationContext } from "context/localization";
import { useTranslationContext } from "context/translation";
import { getEcommerceWithValue } from "helpers/analyticsLogger";
import { useCartAnalyticsEvents } from "hooks/analytics/useCartAnalyticsEvents";
import Image from "next/image";
import Link from "next/link";
import { BundleItemProps } from "./common/types";
import { FlagAndNote } from "./item-children/FlagAndNote";
import { ItemImage } from "./item-children/ItemImage";
import { QuantitySelector } from "./item-children/QuantitySelector";

export const BundleItem: FC<BundleItemProps> = ({
  blockedItem,
  cmsProducts,
  isGuest,
  isLink = true,
  lineItem,
  small,
  currency,
}) => {
  const { cart, removeDynamicBundle, updateCartItem } = useContext(CartContext);
  const { removeFromCartEvent } = useCartAnalyticsEvents();
  const { formatPriceByLocale } = useContext(LocalizationContext);
  const { remove, pack } = useTranslationContext();

  const [isUpdateQtyLoading, setIsUpdateQtyLoading] = useState<
    "increasing" | "decreasing" | false
  >(false);
  const [error, setError] = useState<string>();

  const {
    cartBundleReference,
    imgUrl,
    primaryBundleItems,
    subtotal,
    title,
    bundleId,
    dynamicBundle: { tag: bundleTag },
  } = lineItem;
  const {
    item,
    item: { gtin, id, inventoryQty, isOutOfStock },
  } = primaryBundleItems?.[0] || { item: {} };
  const quantity = 1;
  const formattedSubtotal = formatPriceByLocale(subtotal, true, true);

  const cmsProduct = cmsProducts?.find(
    (product) => product.bundleTags?.includes(bundleTag),
  );

  const itemListName = "Cart";
  const ecommerce = getEcommerceWithValue({
    currency,
    itemListName,
    items: [
      {
        itemId: bundleId,
        gtin: gtin,
        itemName: lineItem.dynamicBundle.tag,
        itemBrand: ITEM_BRAND,
        itemCategory: cmsProduct?.category?.analyticsCategoryName,
        price: subtotal / 100,
        itemListName,
        currency,
        index: 0,
        quantity: 1,
      },
    ],
  });

  const handleRemoveFromCartEvent = async () => {
    const newCart = await updateCartItem({
      productId: bundleId,
      quantity: 0,
      subscriptionIntervalInDays: null,
    });

    removeFromCartEvent(newCart, ecommerce);
  };

  const updateQty = async (newQuantity: number) => {
    if (!id) return;

    try {
      setIsUpdateQtyLoading("decreasing");

      if (newQuantity === 0) {
        await removeDynamicBundle(cart?.id, cartBundleReference);
        await handleRemoveFromCartEvent();
      } else {
        const newCart = await updateCartItem({
          productId: id,
          quantity: newQuantity,
          subscriptionIntervalInDays: null,
        });
        removeFromCartEvent(newCart, ecommerce);
      }
    } catch (err) {
      setError("Something went wrong. Please try again");
    } finally {
      setIsUpdateQtyLoading(false);
    }
  };

  const [isRemoveBundleItemLoading, setIsRemoveBundleItemLoading] =
    useState(false);
  const handleRemove = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!id) return;
    try {
      setIsRemoveBundleItemLoading(true);
      await removeDynamicBundle(cart?.id, cartBundleReference);
      await handleRemoveFromCartEvent();
    } finally {
      setIsRemoveBundleItemLoading(false);
    }
  };

  const outOfStock = isOutOfStock || !inventoryQty || blockedItem?.isOutOfStock;
  const lowStock =
    (!!inventoryQty && quantity > inventoryQty) ||
    (blockedItem?.isLowStock && !!blockedItem?.inventoryQty);
  const remainingInventory = blockedItem?.inventoryQty || inventoryQty || 0;
  const pdpHref = `/${cmsProduct?.category?.slug}/${cmsProduct?.slug}?variant=${bundleTag}`;

  return (
    <div
      className="flex justify-start items-start gap-4 py-6"
      data-testid="cart-item"
    >
      <ItemImage
        isLink={isLink}
        itemImgSrc={imgUrl || ""}
        productTitle={title || ""}
        productLink={pdpHref || ""}
        small={small}
      />
      <div className="flex flex-col flex-1 justify-start items-start gap-6">
        <div className="flex w-full gap-6 justify-between md:justify-start">
          <div className="flex-col items-start gap-1 inline-flex md:w-5/12">
            <div className="flex gap-1 text-xs">
              {pack}
              <Image
                alt="bundle-icon"
                height={18}
                src={`${endpoints.cdn}/static-images/sodastream-dtc/bundle-icon.svg`}
                width={18}
              />
            </div>
            <Link href={pdpHref}>
              <h4 className=" text-zinc-900 text-base font-semibold leading-[21px] text-wrap">
                {title}
              </h4>
            </Link>
          </div>
          <div className="hidden md:block flex-1">
            <QuantitySelector
              isUpdateQtyLoading={isUpdateQtyLoading}
              maxQty={1}
              outOfStock={!!outOfStock}
              quantity={quantity}
              updateQty={updateQty}
            />
          </div>
          <div className="flex flex-col justify-center items-end gap-1">
            <h4 className="text-right text-gray-900 text-base font-semibold leading-[21px]">
              {formattedSubtotal}
            </h4>
            <Button
              variant="unstyled"
              size="small"
              loading={isRemoveBundleItemLoading}
              onClick={handleRemove}
              className="text-blue-950 text-xs underline hidden md:block"
            >
              {remove}
            </Button>
          </div>
        </div>
        <div className="flex w-full gap-2 items-center justify-between md:hidden">
          <QuantitySelector
            isUpdateQtyLoading={isUpdateQtyLoading}
            maxQty={1}
            outOfStock={!!outOfStock}
            quantity={quantity}
            updateQty={updateQty}
          />
          <Button
            variant="unstyled"
            size="small"
            loading={isRemoveBundleItemLoading}
            onClick={handleRemove}
          >
            <CartRemoveItemSvg />
          </Button>
        </div>
        <FlagAndNote
          outOfStock={outOfStock}
          lowStock={lowStock}
          remainingInventory={remainingInventory}
          product={item}
          quantity={quantity}
          inventoryQty={inventoryQty || 0}
          blockedItem={blockedItem}
          subscriptionIntervalInDays={null}
          isGuest={isGuest}
        />
        {error && <Alert type="error">{error}</Alert>}
      </div>
    </div>
  );
};
